import type { SVGProps } from "react";
import {
  IconShieldCheck,
  IconLock,
  IconWifiOff,
  IconCheckCircle,
  IconStar,
} from "@/components/icons";

/**
 * The premium trust bar — one coherent, dark gradient guarantee strip shown
 * directly beneath the document actions (desktop: its own full-width card
 * below the main white card; mobile: a nested dark card in the same
 * position the old trust grid held).
 *
 * Purely informational: no links, no click handlers, no hover-as-button
 * treatment, no focus targets. Every claim here must be verified against the
 * deployed build before shipping (local-only processing, genuine offline
 * support, no auth wall, no payment) — see tasks/landing-redesign.md.
 *
 * One data source drives both layouts, per the spec's "do not duplicate
 * wording for desktop and mobile" rule.
 */
interface TrustItem {
  id: string;
  label: string;
  Icon: (p: SVGProps<SVGSVGElement>) => JSX.Element;
  /** Medallion border + glow colour. */
  accent: string;
  /** Icon stroke colour (lighter tint of the accent for contrast). */
  iconColor: string;
  /** Translucent medallion background tint. */
  ring: string;
}

const TRUST_ITEMS: TrustItem[] = [
  {
    id: "local",
    label: "Your files never leave your device",
    Icon: IconShieldCheck,
    accent: "#25c5ff",
    iconColor: "#bdefff",
    ring: "rgba(37, 197, 255, 0.14)",
  },
  {
    id: "private",
    label: "Private by design",
    Icon: IconLock,
    accent: "#d45cf5",
    iconColor: "#f4c8ff",
    ring: "rgba(212, 92, 245, 0.14)",
  },
  {
    id: "offline",
    label: "Works offline",
    Icon: IconWifiOff,
    accent: "#8f7cff",
    iconColor: "#d8d0ff",
    ring: "rgba(143, 124, 255, 0.14)",
  },
  {
    id: "no-signup",
    label: "No sign up required",
    Icon: IconCheckCircle,
    accent: "#00d783",
    iconColor: "#a8f5d4",
    ring: "rgba(0, 215, 131, 0.14)",
  },
  {
    id: "free",
    label: "Free to use",
    Icon: IconStar,
    accent: "#fdba3b",
    iconColor: "#ffe5a1",
    ring: "rgba(253, 186, 59, 0.14)",
  },
];

export function TrustBar({ variant }: { variant: "desktop" | "mobile" }) {
  const isMobile = variant === "mobile";

  return (
    <section
      aria-label="Why you can trust JDoc"
      className={[
        "trust-bar-gradient relative overflow-hidden",
        isMobile ? "rounded-2xl px-3.5 py-3.5" : "rounded-[20px] px-6 py-3 xl:px-7",
      ].join(" ")}
      style={{ boxShadow: "var(--trust-shadow)" }}
    >
      {/* Slim mango accent along the top edge — decorative only. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: "var(--trust-mango)" }}
      />

      <ul
        role="list"
        className={[
          "m-0 list-none p-0",
          isMobile ? "flex flex-col gap-2" : "grid grid-cols-5 items-stretch",
        ].join(" ")}
      >
        {TRUST_ITEMS.map(({ id, label, Icon, accent, iconColor, ring }, i) => (
          <li
            key={id}
            className={[
              "flex min-w-0 items-center",
              isMobile
                ? "gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5"
                : "gap-4 px-4",
              !isMobile && i > 0 ? "border-l" : "",
            ].join(" ")}
            style={!isMobile && i > 0 ? { borderColor: "var(--trust-divider)" } : undefined}
          >
            <span
              aria-hidden="true"
              className={[
                "flex shrink-0 items-center justify-center rounded-full",
                isMobile ? "h-10 w-10 border-[1.5px]" : "h-[54px] w-[54px] border-2",
              ].join(" ")}
              style={{
                borderColor: accent,
                background: ring,
                color: iconColor,
                boxShadow: isMobile ? undefined : `inset 0 0 14px ${ring}, 0 0 16px ${ring}`,
              }}
            >
              <Icon className={isMobile ? "h-5 w-5" : "h-6 w-6"} strokeWidth={2} />
            </span>
            <span
              className={[
                "font-semibold leading-snug text-white",
                isMobile ? "text-sm" : "text-[clamp(0.98rem,1.12vw,1.18rem)]",
              ].join(" ")}
            >
              {label}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
